import {
  computeDistanceBetween, computeOffset,
} from 'spherical-geometry-js';
import type { LatLng } from '~/_shared/types/latLng';
import { convertUnitToMeters } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { BoundingBox } from '~/map/map/boundingBox';
import type { PlacesSearchResult } from '~/store/frontendState/places/places.state';
import type { PlacesSearch } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import type { PlacesSearchRectangle } from './places.repository';

export const placesSearchToPlacesSearchRectangle = (search: PlacesSearch): PlacesSearchRectangle => {
  const { center: { latLng }, distance } = search;
  const distanceInMeters = convertUnitToMeters(distance.value, distance.unit);
  return {
    low: {
      latitude: parseFloat(computeOffset(latLng, distanceInMeters, 180).lat().toFixed(6)),
      longitude: parseFloat(computeOffset(latLng, distanceInMeters, 270).lng().toFixed(6)),
    },
    high: {
      latitude: parseFloat(computeOffset(latLng, distanceInMeters, 0).lat().toFixed(6)),
      longitude: parseFloat(computeOffset(latLng, distanceInMeters, 90).lng().toFixed(6)),
    },
  };
};

export const getClosestDistanceFromCenterToBoundsEdge = (
  center: LatLng,
  bounds: BoundingBox,
): number => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const distanceToNorthEdge = computeDistanceBetween(center, { lat: ne.lat, lng: center.lng });
  const distanceToSouthEdge = computeDistanceBetween(center, { lat: sw.lat, lng: center.lng });
  const distanceToEastEdge = computeDistanceBetween(center, { lat: center.lat, lng: ne.lng });
  const distanceToWestEdge = computeDistanceBetween(center, { lat: center.lat, lng: sw.lng });

  return Math.min(
    distanceToNorthEdge,
    distanceToSouthEdge,
    distanceToEastEdge,
    distanceToWestEdge,
  );
};

export const getPlacesSearchResultBoundingBox = ({
  searchResult,
}: {
  searchResult?: PlacesSearchResult;
}): BoundingBox | null => {
  if (!searchResult || !searchResult.items.length) {
    return null;
  }

  const boundingBox = new BoundingBox();
  searchResult.items.forEach((place) => {
    boundingBox.extend(place.location);
  });

  return boundingBox;
};

export const convertPlacesSearchRectangleToBoundingBox = (
  rectangle: PlacesSearchRectangle,
): BoundingBox => {
  const boundingBox = new BoundingBox();
  boundingBox.extend({
    lat: rectangle.low.latitude,
    lng: rectangle.low.longitude,
  });
  boundingBox.extend({
    lat: rectangle.high.latitude,
    lng: rectangle.high.longitude,
  });
  return boundingBox;
};

export const convertPlacesSearchToBoundingBox = (search: PlacesSearch): BoundingBox => {
  const rectangle = placesSearchToPlacesSearchRectangle(search);

  return convertPlacesSearchRectangleToBoundingBox(rectangle);
};
